/**
 * Scan-handling settings — device-local (b17a).
 *
 * The three "Scan handling" controls Sam asked for live here. They are
 * device-local (localStorage) by design, mirroring `dexStateStore`: the Dex
 * state these settings govern is itself device-local, and the toggles must work
 * offline / signed-out, with no server roundtrip. (The server-backed
 * `accountSettings` table is for cross-device account preferences and requires
 * sign-in; reconciliation behaviour must not.)
 *
 *   - autoUpdateCaught   — the scan additively marks caught entries. LOCKED ON:
 *                          `normalize()` ignores any stored value so this is
 *                          always true. The user-facing toggle was removed (the
 *                          scan must not be disableable); the field is kept so
 *                          the live-scan hook's gate reads a stable `true`.
 *   - suggestGaps        — "Suggest gaps from scan". When ON (default) a
 *                          high-confidence "not caught" reading that disagrees
 *                          with a manual "caught" surfaces a conflict
 *                          toast/review card. When OFF, conflict surfacing is
 *                          disabled entirely.
 *   - confidenceProfile  — "Confidence threshold": Standard / Conservative /
 *                          Aggressive. Controls how clean a "not caught" read
 *                          must be before it surfaces as a gap. See
 *                          [[dexScanConfidence]] CONFIDENCE_THRESHOLDS.
 *
 * Defaults match the product objective ("most accurate and up-to-date mirror"):
 * additive auto-update ON, gap suggestions ON, Standard sensitivity.
 */

import {
  DEFAULT_CONFIDENCE_PROFILE,
  type ConfidenceProfile,
} from "./dexScanConfidence";

export const DEX_SCAN_SETTINGS_EVENT = "nexus:dex-scan-settings-changed";
const STORAGE_KEY = "nexus.dexScanSettings.v1";

export interface DexScanSettings {
  /** Additively mark caught entries from a scan. Default true. */
  autoUpdateCaught: boolean;
  /** Surface high-confidence "you may not have this yet" gaps. Default true. */
  suggestGaps: boolean;
  /** Sensitivity profile for gap suggestions. Default "standard". */
  confidenceProfile: ConfidenceProfile;
  /**
   * 2026-09-03: during a My-Pokémon live scan, copy each Pokémon's name in
   * the trainer's naming template to the clipboard, once per Pokémon, the
   * moment its facts are all read. Lets the trainer leave the appraisal,
   * open the name field and paste, without the scan re-copying the same
   * Pokémon on every later frame. Default true.
   */
  copyNicknameOnScan: boolean;
}

export const DEFAULT_DEX_SCAN_SETTINGS: DexScanSettings = {
  autoUpdateCaught: true,
  suggestGaps: true,
  confidenceProfile: DEFAULT_CONFIDENCE_PROFILE,
  copyNicknameOnScan: true,
};

const VALID_PROFILES: ReadonlySet<ConfidenceProfile> = new Set<ConfidenceProfile>([
  "standard",
  "conservative",
  "aggressive",
]);

/** Coerce arbitrary parsed JSON into a complete, valid settings object. */
function normalize(raw: unknown): DexScanSettings {
  const base = { ...DEFAULT_DEX_SCAN_SETTINGS };
  if (!raw || typeof raw !== "object") return base;
  const o = raw as Record<string, unknown>;
  // autoUpdateCaught is intentionally NOT read from storage: the scan's caught
  // auto-update is always on and there is no user control to disable it (the
  // Settings "Scan" block was removed). Forcing the default here ignores any
  // legacy persisted `false`, so a device that toggled it off before the block
  // was removed is not left with a permanently dead scan.
  if (typeof o.suggestGaps === "boolean") base.suggestGaps = o.suggestGaps;
  if (typeof o.copyNicknameOnScan === "boolean") base.copyNicknameOnScan = o.copyNicknameOnScan;
  if (typeof o.confidenceProfile === "string" && VALID_PROFILES.has(o.confidenceProfile as ConfidenceProfile)) {
    base.confidenceProfile = o.confidenceProfile as ConfidenceProfile;
  }
  return base;
}

function emitChange(): void {
  if (typeof window === "undefined") return;
  try {
    window.dispatchEvent(new Event(DEX_SCAN_SETTINGS_EVENT));
  } catch {
    /* swallow */
  }
}

/** Read the current settings (defaults when nothing is persisted). */
export function getDexScanSettings(): DexScanSettings {
  if (typeof localStorage === "undefined") return { ...DEFAULT_DEX_SCAN_SETTINGS };
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_DEX_SCAN_SETTINGS };
    return normalize(JSON.parse(raw));
  } catch {
    return { ...DEFAULT_DEX_SCAN_SETTINGS };
  }
}

/** Patch one or more fields, persist, and notify subscribers. */
export function setDexScanSettings(patch: Partial<DexScanSettings>): DexScanSettings {
  const next = normalize({ ...getDexScanSettings(), ...patch });
  if (typeof localStorage !== "undefined") {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      /* quota — degrade to in-memory defaults on next read */
    }
  }
  emitChange();
  return next;
}

/** Test-only reset. */
export function _resetDexScanSettingsForTests(): void {
  if (typeof localStorage === "undefined") return;
  localStorage.removeItem(STORAGE_KEY);
  emitChange();
}
